import React, { memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ListTree, Plus, X, ChevronRight, ChevronDown, CornerDownRight } from "lucide-react";
import { confirmDialog } from "../../lib/confirm";

// Yeni Görev formu için "bekleyen" alt görevler — henüz oluşturulmaz; görev
// oluşturulduğunda parent (AddTaskModal) tüm ağacı tek PATCH ile kaydeder.
// Desen `PendingAttachments` ile aynıdır (bellekte taslak + görev oluşunca gider).
//
// PERFORMANS (Faz 13 kuralı): satır metinleri/tarihleri STATE'te DEĞİL, paylaşılan
// bir `valuesRef` Map'inde tutulur. Bir satıra harf yazmak hiçbir yeniden render
// tetiklemez; state yalnızca satır EKLEME/KALDIRMA/KATLAMA'da değişir.

const yeniId = () => `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const bosDeger = () => ({ text: "", start_date: "", due_date: "", completed_at: "" });

// Girinti: okunabilirliği korumak için 4. seviyeden sonra SABİTLENİR
// (satırlar ekran dışına kaymaz).
const girinti = (depth) => Math.min(depth, 4) * 14;

const sayAlt = (node) => (node.children || []).reduce((t, c) => t + 1 + sayAlt(c), 0);

const inputCls =
  "w-full bg-sertex-surface/60 border border-sertex-cyan/25 rounded px-2 py-1 text-[12px] font-mono text-sertex-text placeholder:text-sertex-textMuted/60 focus:border-sertex-cyan outline-none";

// --- Tek taslak satır. memo + stabil handler'lar: kardeşler yeniden çizilmez.
const DraftRowBase = ({ node, depth, valuesRef, onEnter, onAddChild, onRemove, onFocusReady }) => {
  const [acik, setAcik] = useState(false);
  const inputRef = useRef(null);
  const d = valuesRef.current.get(node.id) || bosDeger();

  useEffect(() => {
    if (onFocusReady && onFocusReady() === node.id) inputRef.current?.focus();
  }, [node.id, onFocusReady]);

  // Kontrollü olmayan (uncontrolled) girişler: yazmak render tetiklemez.
  const yaz = useCallback(
    (alan) => (e) => {
      const mevcut = valuesRef.current.get(node.id) || bosDeger();
      valuesRef.current.set(node.id, { ...mevcut, [alan]: e.target.value });
    },
    [node.id, valuesRef],
  );

  const cocukSayisi = sayAlt(node);

  return (
    <div style={{ marginLeft: girinti(depth) }} data-testid={`pending-sub-${node.id}`}>
      <div className="flex items-center gap-1">
        {depth > 0 && <CornerDownRight className="h-3 w-3 text-violet-300/70 shrink-0" />}
        <button
          type="button"
          onClick={() => setAcik((v) => !v)}
          data-testid={`pending-sub-expand-${node.id}`}
          title={acik ? "Alanları katla" : "Tüm alanları aç"}
          className="shrink-0 h-5 w-4 flex items-center justify-center text-sertex-cyan/70 hover:text-sertex-cyan transition-colors"
        >
          {acik ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        <input
          ref={inputRef}
          defaultValue={d.text}
          onChange={yaz("text")}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); onEnter(node.id); }
          }}
          placeholder="Alt görev adı… (Enter: alt satır)"
          data-testid={`pending-sub-text-${node.id}`}
          className={`flex-1 min-w-0 ${inputCls}`}
        />
        <button
          type="button"
          onClick={() => onRemove(node.id, cocukSayisi)}
          data-testid={`pending-sub-remove-${node.id}`}
          title="Kaldır"
          className="shrink-0 p-1 text-sertex-textMuted hover:text-rose-400 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {acik && (
        <div className="mt-1 mb-1 ml-5 grid grid-cols-3 gap-1.5" data-testid={`pending-sub-fields-${node.id}`}>
          <div>
            <div className="hud-text text-sertex-textMuted/80 mb-0.5">BAŞLANGIÇ</div>
            <input
              type="datetime-local"
              defaultValue={d.start_date}
              onChange={yaz("start_date")}
              data-testid={`pending-sub-start-${node.id}`}
              className={inputCls}
            />
          </div>
          <div>
            <div className="hud-text text-sertex-textMuted/80 mb-0.5">BİTİŞ (HATIRLATICI)</div>
            <input
              type="datetime-local"
              defaultValue={d.due_date}
              onChange={yaz("due_date")}
              data-testid={`pending-sub-due-${node.id}`}
              className={inputCls}
            />
          </div>
          <div>
            <div className="hud-text text-sertex-textMuted/80 mb-0.5">TAMAMLANMA</div>
            <input
              type="datetime-local"
              defaultValue={d.completed_at}
              onChange={yaz("completed_at")}
              data-testid={`pending-sub-completed-${node.id}`}
              className={inputCls}
            />
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => onAddChild(node.id)}
        data-testid={`pending-sub-addchild-${node.id}`}
        title="Bu satırın ALTINA girintili bir çocuk alt görev ekler"
        className="ml-9 mb-1 inline-flex items-center gap-1 px-1 rounded border border-violet-400/30 text-violet-300/80 hover:text-violet-200 hover:bg-violet-500/10 hud-text transition-colors"
      >
        <CornerDownRight className="h-2.5 w-2.5" /> İÇİNE EKLE
      </button>

      {(node.children || []).map((c) => (
        <DraftRow
          key={c.id}
          node={c}
          depth={depth + 1}
          valuesRef={valuesRef}
          onEnter={onEnter}
          onAddChild={onAddChild}
          onRemove={onRemove}
          onFocusReady={onFocusReady}
        />
      ))}
    </div>
  );
};
const DraftRow = memo(DraftRowBase);

export const PendingSubtasks = ({ apiRef }) => {
  const [tree, setTree] = useState([]);
  const valuesRef = useRef(new Map());
  const odakRef = useRef(null);

  const focusReady = useCallback(() => odakRef.current, []);

  const ekle = useCallback((parentId = null, afterId = null) => {
    const id = yeniId();
    valuesRef.current.set(id, bosDeger());
    odakRef.current = id;
    setTree((prev) => {
      const yeni = { id, children: [] };
      if (!parentId && !afterId) return [...prev, yeni];
      const gez = (liste) => {
        const out = [];
        for (const n of liste) {
          if (n.id === parentId) {
            out.push({ ...n, children: [...(n.children || []), yeni] });
            continue;
          }
          const kopya = { ...n, children: gez(n.children || []) };
          out.push(kopya);
          if (n.id === afterId) out.push(yeni);
        }
        return out;
      };
      return gez(prev);
    });
  }, []);

  const kaldir = useCallback(async (id, cocukSayisi) => {
    if (cocukSayisi > 0) {
      const ok = await confirmDialog({
        title: "Alt Görevi Kaldır",
        message: `Bu alt görevin altındaki ${cocukSayisi} öğe de kaldırılacak, devam edilsin mi?`,
        confirmText: "Kaldır",
      });
      if (!ok) return;
    }
    setTree((prev) => {
      const gez = (liste) =>
        liste.filter((n) => {
          if (n.id !== id) return true;
          // Kaldırılan satırın (ve altının) değerleri de temizlenir; aksi halde
          // "kaydedilmemiş alt görev var" uyarısı hayalet isimle tetiklenir.
          const sil = (x) => { valuesRef.current.delete(x.id); (x.children || []).forEach(sil); };
          sil(n);
          return false;
        }).map((n) => ({ ...n, children: gez(n.children || []) }));
      return gez(prev);
    });
  }, []);

  const enter = useCallback((id) => ekle(null, id), [ekle]);
  const cocukEkle = useCallback((id) => ekle(id, null), [ekle]);

  // Parent'a imperative erişim: kaydetme ve kapatma uyarısı için.
  useImperativeHandle(apiRef, () => ({
    // İsimsiz satırlar atlanır; ALTINDAKİ isimli çocuklar ÜST SEVİYEYE taşınır
    // (veri kaybı olmaz). Kaç satır atlandığı ve kaç öğe taşındığı raporlanır.
    derle: () => {
      let atlanan = 0;
      let tasinan = 0;
      const gez = (liste) => {
        const out = [];
        for (const n of liste) {
          const d = valuesRef.current.get(n.id) || bosDeger();
          const cocuklar = gez(n.children || []);
          const ad = (d.text || "").trim();
          if (!ad) {
            atlanan += 1;
            tasinan += cocuklar.length;
            out.push(...cocuklar); // çocukları bir üst seviyeye taşı
            continue;
          }
          const dugum = { text: ad };
          if (d.start_date) dugum.start_date = new Date(d.start_date).toISOString();
          if (d.due_date) dugum.due_date = new Date(d.due_date).toISOString();
          if (d.completed_at) dugum.completed_at = new Date(d.completed_at).toISOString();
          if (cocuklar.length) dugum.children = cocuklar;
          out.push(dugum);
        }
        return out;
      };
      const agac = gez(tree);
      return { agac, atlanan, tasinan };
    },
    isimliVarMi: () => {
      let bulundu = false;
      const gez = (liste) => {
        for (const n of liste) {
          if ((valuesRef.current.get(n.id)?.text || "").trim()) { bulundu = true; return; }
          gez(n.children || []);
          if (bulundu) return;
        }
      };
      gez(tree);
      return bulundu;
    },
    // FAZ G — taslak için HAM ağaç (boş satırlar dahil, hiyerarşi ve sıra korunur).
    taslakAl: () => {
      const gez = (liste) => liste.map((n) => {
        const d = valuesRef.current.get(n.id) || bosDeger();
        return {
          text: d.text || "",
          start_date: d.start_date || "",
          due_date: d.due_date || "",
          completed_at: d.completed_at || "",
          children: gez(n.children || []),
        };
      });
      return gez(tree);
    },
    taslakYukle: (agac) => {
      if (!Array.isArray(agac)) return;
      valuesRef.current.clear();
      const gez = (liste) => liste.map((d) => {
        const id = yeniId();
        valuesRef.current.set(id, {
          text: d.text || "",
          start_date: d.start_date || "",
          due_date: d.due_date || "",
          completed_at: d.completed_at || "",
        });
        return { id, children: gez(d.children || []) };
      });
      setTree(gez(agac));
    },
  }), [tree]);

  return (
    <div className="border-t border-sertex-cyan/15 pt-1.5" data-testid="pending-subtasks">
      <div className="flex items-center justify-between mb-1">
        <div className="hud-text text-sertex-textMuted flex items-center gap-1">
          <ListTree className="h-3 w-3" /> ALT GÖREVLER
        </div>
        <button
          type="button"
          onClick={() => ekle(null, null)}
          data-testid="pending-subtask-add-btn"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-sertex-cyan/30 text-sertex-cyan hover:bg-sertex-cyan/10 hud-text transition-colors"
        >
          <Plus className="h-3 w-3" /> KÖK ALT GÖREV EKLE
        </button>
      </div>
      {tree.length > 0 && (
        <div className="space-y-0.5" data-testid="pending-subtask-list">
          {tree.map((n) => (
            <DraftRow
              key={n.id}
              node={n}
              depth={0}
              valuesRef={valuesRef}
              onEnter={enter}
              onAddChild={cocukEkle}
              onRemove={kaldir}
              onFocusReady={focusReady}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingSubtasks;
